import {
  useId,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import {
  digitsOnly,
  formatIntegerInput,
  formatPhoneInput,
} from "../../lib/inputFormat";
import { CustomSelect } from "./CustomSelect";

interface BaseProps {
  label: string;
  hint?: string;
  error?: string;
  children?: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: BaseProps & { htmlFor?: string }) {
  return (
    <div className={`field ${error ? "field--error" : ""}`}>
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="field__error">{error}</span>
      ) : hint ? (
        <span className="field__hint">{hint}</span>
      ) : null}
    </div>
  );
}

export function Input({
  label,
  hint,
  error,
  id,
  className = "",
  type = "text",
  value,
  defaultValue,
  onChange,
  inputMode,
  maxLength,
  ...props
}: BaseProps & InputHTMLAttributes<HTMLInputElement>) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const messageId = `${fieldId}-message`;
  const format =
    type === "tel" ? "phone" : type === "number" ? "integer" : null;
  const formatValue = (current: string | number | readonly string[]) => {
    if (Array.isArray(current)) return current;
    if (format === "phone") return formatPhoneInput(String(current));
    if (format === "integer") return formatIntegerInput(String(current));
    return current;
  };

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (format === "phone") {
      event.currentTarget.value = formatPhoneInput(event.currentTarget.value);
    } else if (format === "integer") {
      event.currentTarget.value = digitsOnly(event.currentTarget.value);
    }
    onChange?.(event);
  }

  return (
    <Field label={label} htmlFor={fieldId}>
      <input
        id={fieldId}
        className={`input ${className}`.trim()}
        type={format === "integer" ? "text" : type}
        inputMode={
          inputMode ??
          (format === "phone"
            ? "tel"
            : format === "integer"
              ? "numeric"
              : undefined)
        }
        value={value === undefined ? undefined : formatValue(value)}
        defaultValue={
          defaultValue === undefined ? undefined : formatValue(defaultValue)
        }
        onChange={handleChange}
        maxLength={format === "phone" ? (maxLength ?? 15) : maxLength}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={error || hint ? messageId : undefined}
        {...props}
      />
      {(error || hint) && (
        <span id={messageId} className={error ? "field__error" : "field__hint"}>
          {error ?? hint}
        </span>
      )}
    </Field>
  );
}

export function Select({
  label,
  hint,
  error,
  children,
  id,
  className = "",
  ...props
}: BaseProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const messageId = `${fieldId}-message`;
  return (
    <Field label={label} htmlFor={fieldId}>
      <CustomSelect
        id={fieldId}
        className={`input ${className}`.trim()}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={error || hint ? messageId : undefined}
        label={label}
        {...props}
      >
        {children}
      </CustomSelect>
      {(error || hint) && (
        <span id={messageId} className={error ? "field__error" : "field__hint"}>
          {error ?? hint}
        </span>
      )}
    </Field>
  );
}

export function Textarea({
  label,
  hint,
  error,
  id,
  className = "",
  ...props
}: BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const messageId = `${fieldId}-message`;
  return (
    <Field label={label} htmlFor={fieldId}>
      <textarea
        id={fieldId}
        className={`input input--textarea ${className}`.trim()}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={error || hint ? messageId : undefined}
        {...props}
      />
      {(error || hint) && (
        <span id={messageId} className={error ? "field__error" : "field__hint"}>
          {error ?? hint}
        </span>
      )}
    </Field>
  );
}
